import { QueryClient } from '@tanstack/react-query';
import React from 'react';
import { PagePolicies } from '~/utils/roles';

import PayoutDetailsRoute from '../../../payouts/PayoutDetailsRoute';
import { UserDetailError } from '../components/UserDetailError';
import { createAdminUserDetailLoader } from './loaders';
import { BetaSubscriptionsRoute, SubscriptionsRoute } from './subscriptionsRoutes';
import { AdminRouteObject, AdminUserDetailPaths } from './types';

// Lazy load the main router entry to maintain consistency with MainRoutes.tsx
const AdminUserDetailRouterEntry = React.lazy(() => import('./router.entry'));

const AdminBackToFundedTab = React.lazy(() => import('../../components/backToFundedTab'));
const CertificatesTab = React.lazy(() => import('../../components/certificatesTab'));
const EmailLog = React.lazy(() => import('../../components/emailTab.crystal'));
const CaseLogTab = React.lazy(() => import('../../components/case-log/CaseLogTab'));
const KycTabRoute = React.lazy(() => import('../../components/kycTabRoute'));
const LiveAccountsTab = React.lazy(() => import('../../components/liveAccountsTab'));
const ResetCreditsTab = React.lazy(() => import('../../components/resetCreditsTab'));
const TaxTab = React.lazy(() => import('../../components/taxTab'));
const UserNotes = React.lazy(() => import('../../components/userNotesTab'));
const UserPaymentMethodsTable = React.lazy(
  () => import('../../components/userPaymentMethodsTable.crystal'),
);
const AdminUserAccounts = React.lazy(() => import('../views/accounts'));
const AuthStateTab = React.lazy(() => import('../views/auth-state/AuthStateTab'));
// TODO: These components will be replaced with enhanced versions in separate feature branches
// - feature/user-overview-improvements: Enhanced overview with stats and user actions
// - feature/user-detail-form-improvements: Enhanced form with validation and better UX
const PersonalTab = React.lazy(() => import('../../components/personalTab'));
const TraderProfileTab = React.lazy(() => import('../../components/traderProfileTab'));
const UserOverviewTab = React.lazy(() => import('../../components/overviewTab.crystal'));
// Use legacy details page until new account-details module is ready
const Details = React.lazy(() => import('../../../accounts/components/details'));
const DateDetails = React.lazy(() => import('../../../accounts/components/dateDetails'));
const AdminPayouts = React.lazy(() => import('../../../payouts'));
const Banking = React.lazy(() => import('../../../payouts/Banking'));
const AdminUserPurchasesTab = React.lazy(() => import('../../components/purchasesTab'));

/**
 * Creates admin user detail routes with React Query loader
 * @param queryClient - React Query client for data prefetching
 * @returns Array of route objects with loaders configured
 */
export const createAdminUserDetailRoutes = (queryClient: QueryClient): AdminRouteObject[] => [
  {
    path: AdminUserDetailPaths.base,
    element: <AdminUserDetailRouterEntry />,
    loader: createAdminUserDetailLoader(queryClient),
    errorElement: <UserDetailError />,

    children: [
      {
        element: <UserOverviewTab />,
        index: true,
        handle: {
          permissions: PagePolicies.AdminUsersPage,
        },
      },
      {
        path: AdminUserDetailPaths.overview,
        element: <UserOverviewTab />,
        handle: {
          permissions: PagePolicies.AdminUsersPage,
        },
      },
      {
        path: AdminUserDetailPaths.details,
        element: <PersonalTab />,
        handle: {
          permissions: PagePolicies.AdminUsersPage,
        },
      },
      {
        path: 'accounts',
        element: <AdminUserAccounts />,
        handle: {
          permissions: PagePolicies.AdminUsersPage,
        },
      },
      {
        path: 'accounts/:accountId',
        element: <Details />,
        handle: {
          permissions: PagePolicies.AdminUsersPage,
        },
      },
      {
        path: 'accounts/:accountId/trades/day/:date',
        element: <DateDetails />,
        handle: {
          permissions: PagePolicies.AdminUsersPage,
        },
      },
      {
        path: 'transactions',
        element: <AdminUserPurchasesTab />,
        handle: {
          permissions: PagePolicies.AdminUsersPage,
        },
      },
      {
        path: 'subscriptions',
        element: <SubscriptionsRoute />,
        handle: {
          permissions: PagePolicies.AdminUsersPage,
        },
      },
      {
        path: 'beta/auth-state',
        element: <AuthStateTab />,
        handle: {
          permissions: PagePolicies.BetaPages,
        },
      },
      {
        path: 'beta/subscriptions',
        element: <BetaSubscriptionsRoute />,
        handle: {
          permissions: PagePolicies.AdminUsersPage,
        },
      },
      {
        path: 'tax',
        element: <TaxTab />,
        handle: {
          permissions: PagePolicies.AdminUsersPage,
        },
      },
      {
        path: AdminUserDetailPaths.resetCredits,
        element: <ResetCreditsTab />,
        handle: {
          permissions: PagePolicies.AdminUsersPage,
        },
      },
      {
        path: AdminUserDetailPaths.investigationLog,
        element: <CaseLogTab />,
        handle: {
          permissions: PagePolicies.AdminTraderCaseLogTab,
        },
      },
      {
        path: AdminUserDetailPaths.emailLog,
        element: <EmailLog />,
        handle: {
          permissions: PagePolicies.AdminUsersPage,
        },
      },
      {
        path: 'certificates',
        element: <CertificatesTab />,
        handle: {
          permissions: PagePolicies.AdminUsersPage,
        },
      },
      {
        path: 'idv',
        element: <KycTabRoute />,
        handle: {
          permissions: PagePolicies.AdminUsersPage,
        },
      },
      {
        path: 'notes',
        element: <UserNotes />,
        handle: {
          permissions: PagePolicies.AdminUserNotesPage,
        },
      },
      {
        path: 'payment-methods',
        element: <UserPaymentMethodsTable />,
        handle: {
          permissions: PagePolicies.AdminPaymentMethodsPage,
        },
      },
      {
        path: 'payouts',
        element: <AdminPayouts />,
        handle: {
          permissions: PagePolicies.AdminUsersPage,
        },
      },
      {
        path: 'payouts/:payoutId',
        element: <PayoutDetailsRoute />,
        handle: {
          permissions: PagePolicies.AdminUsersPage,
        },
      },
      {
        path: 'banking',
        element: <Banking />,
        handle: {
          permissions: PagePolicies.AdminUsersPage,
        },
      },
      {
        path: 'back-to-funded',
        element: <AdminBackToFundedTab />,
        handle: {
          permissions: PagePolicies.AdminUsersPage,
        },
      },
      {
        path: 'live-accounts',
        element: <LiveAccountsTab />,
        handle: {
          permissions: PagePolicies.AdminUsersPage,
        },
      },
      {
        path: AdminUserDetailPaths.traderProfile,
        element: <TraderProfileTab />,
        handle: {
          permissions: PagePolicies.AdminUsersPage,
        },
      },
    ],
  },
];
